import { IMudancaSelecionada } from '@/domain/ops/interfaces/ops';
import { appServiceClient } from '@/infrastructure/http/httpClient';

import {
  GradeSemanas,
  HorizonteMapa,
  MapaResult,
  MovimentoPayload,
  PreviaDaTrocaDeCelula,
  SimulacaoResult,
  TrocaCelulaPayload,
} from '../interfaces/mapa';

interface MapaFiltros {
  marca?: string;
  /** Client (legal name): brings every brand of that client. */
  cliente?: string;
  celula?: string;
  horizonte?: HorizonteMapa;
}

/** Only filters with a value travel: an empty one would narrow the map down to nothing. */
function withQuery(path: string, filtros: MapaFiltros): string {
  const params = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== '') {
      params.set(chave, valor);
    }
  }
  const qs = params.toString();
  return qs !== '' ? `${path}?${qs}` : path;
}

class MapaRepository {
  async getMapa(filtros: MapaFiltros = {}): Promise<MapaResult> {
    return appServiceClient.get<MapaResult>(withQuery('mapa', filtros));
  }

  /** The week x cell grid. It always covers the whole portfolio, so it takes no horizon. */
  async getGradeSemanas(filtros: Omit<MapaFiltros, 'horizonte'> = {}): Promise<GradeSemanas> {
    return appServiceClient.get<GradeSemanas>(withQuery('mapa/grade-semanas', filtros));
  }

  async simular(payload: MovimentoPayload): Promise<SimulacaoResult> {
    return appServiceClient.post<SimulacaoResult>('mapa/simular-movimento', { body: payload });
  }

  async aplicar(payload: MovimentoPayload): Promise<MapaResult> {
    return appServiceClient.post<MapaResult>('mapa/aplicar-movimento', { body: payload });
  }

  /**
   * O Mapa como ele FICARIA com as mudanças marcadas da proposta: o lado "Proposta" do
   * seletor. Leva os mesmos filtros da tela, senão as duas leituras não seriam
   * comparáveis. Não grava nada.
   */
  async getMapaDaProposta(
    mudancas: Array<IMudancaSelecionada>,
    filtros: MapaFiltros = {},
  ): Promise<MapaResult> {
    return appServiceClient.post<MapaResult>('mapa/proposta', {
      body: { ...filtros, mudancas },
    });
  }

  /**
   * A grade "Por semana" como ela FICARIA com as mudanças marcadas: o lado "Proposta" do
   * seletor na outra aba. Mesmos filtros da tela, e nada é gravado.
   */
  async getGradeDaProposta(
    mudancas: Array<IMudancaSelecionada>,
    filtros: Omit<MapaFiltros, 'horizonte'> = {},
  ): Promise<GradeSemanas> {
    return appServiceClient.post<GradeSemanas>('mapa/grade-semanas/proposta', {
      body: { ...filtros, mudancas },
    });
  }

  /** The preview of a cell-only change, for the OP card. Saves nothing. */
  async simularCelula(payload: TrocaCelulaPayload): Promise<PreviaDaTrocaDeCelula> {
    return appServiceClient.post<PreviaDaTrocaDeCelula>('mapa/simular-celula', { body: payload });
  }
}

export const mapaRepository = new MapaRepository();
